import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Vendor, Shop, Schedule, Promotion, Item, ItemType, ItemCategoryType, DurationType, SubscriptionType, SubscriptionCategory, CardType } from '../../services/vendor/vendor.model';
import { VendorService } from '../../services/vendor/vendor.service';
import { AzureStorageService } from '../../services/azure-storage/azure-storage.service';

@Component({
    selector: 'app-vendor-form',
    templateUrl: './vendor-form.component.html',
    styleUrls: ['./vendor-form.component.scss'],
})
export class VendorFormComponent implements OnInit {
    vendorForm: FormGroup;
    isEdit = false;
    isSubmitting = false;
    days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    selectedFile: File | null = null;
    isUploading = false;

    selectedIconFile: File | null = null;
    isUploadingIcon = false;

    constructor(
        private fb: FormBuilder,
        private vendorService: VendorService,
        private snackBar: MatSnackBar,
        private dialogRef: MatDialogRef<VendorFormComponent>,
        private azureStorageService: AzureStorageService,
        @Inject(MAT_DIALOG_DATA) public data: { vendor: Vendor | null },
    ) {
        this.vendorForm = this.createVendorForm();
        this.isEdit = !!data.vendor;
        if (this.isEdit && data.vendor) {
            this.patchFormValues(data.vendor);
        }
    }

    ngOnInit(): void {}

    createVendorForm(): FormGroup {
        return this.fb.group({
            name: this.fb.group({
                en: ['', Validators.required],
                fr: ['', Validators.required],
            }),
            allowedOrganisation: this.fb.array([]),
            deleted: [false],
            shops: this.fb.array([]),
        });
    }

    patchFormValues(vendor: Vendor): void {
        // Clear arrays before patching
        while (this.allowedOrganisations.length) {
            this.allowedOrganisations.removeAt(0);
        }

        while (this.shops.length) {
            this.shops.removeAt(0);
        }

        // Patch main form
        this.vendorForm.patchValue({
            name: vendor.name,
            deleted: vendor.deleted,
        });

        // Patch allowed organizations
        vendor.allowedOrganisation.forEach((org) => {
            this.allowedOrganisations.push(this.fb.control(org));
        });

        // Patch shops
        vendor.shops.forEach((shop) => {
            this.shops.push(this.createShopForm(shop));
        });
    }

    // Getter for easier access to form arrays
    get allowedOrganisations(): FormArray {
        return this.vendorForm.get('allowedOrganisation') as FormArray;
    }

    get shops(): FormArray {
        return this.vendorForm.get('shops') as FormArray;
    }

    // Add a new organization
    addOrganisation(): void {
        this.allowedOrganisations.push(this.fb.control(''));
    }

    // Remove an organization
    removeOrganisation(index: number): void {
        this.allowedOrganisations.removeAt(index);
    }

    // Get organizations FormArray for a specific shop
    getShopOrganisations(shopIndex: number): FormArray {
        return (this.shops.at(shopIndex) as FormGroup).get('organisations') as FormArray;
    }

    // Add a new organisation to a shop
    addShopOrganisation(shopIndex: number): void {
        const organisationsArray = this.getShopOrganisations(shopIndex);
        organisationsArray.push(this.fb.control(''));
    }

    // Remove an organisation from a shop
    removeShopOrganisation(shopIndex: number, organisationIndex: number): void {
        const organisationsArray = this.getShopOrganisations(shopIndex);
        organisationsArray.removeAt(organisationIndex);
    }

    createShopForm(shop?: Shop): FormGroup {
        const shopForm = this.fb.group({
            _id: [shop?._id || ''],
            name: this.fb.group({
                en: ['', Validators.required],
                fr: ['', Validators.required],
            }),
            description: this.fb.group({
                en: [''],
                fr: [''],
            }),
            gatewayToken: [''],
            organisations: this.fb.array([]),
            website: [''],
            onHomepage: [false],
            order: [1],
            timezone: ['America/Montreal'],
            likes: [0],
            pictures: this.fb.array([]),
            icon: [''],
            shopCategory: this.fb.group({
                en: [''], // Change to string control instead of FormArray
                fr: [''], // Change to string control instead of FormArray
            }),
            deleted: [false],
            items: this.fb.array([]),
            taxNumbers: this.fb.array([]),
            promotions: this.fb.array([]),
            address: this.fb.group({
                street: [''],
                streetName: [''],
                number: [''],
                postCode: [''],
                city: [''],
                province: [''],
                country: [''],
                coordinates: this.fb.group({
                    type: ['Point'],
                    coordinates: this.fb.array([
                        this.fb.control(0), // Wrap in FormControl
                        this.fb.control(0), // Wrap in FormControl
                    ]),
                }),
                typeStreet: [''],
                direction: [''],
                unit: [''],
            }),
            contact: this.fb.group({
                phone: [''],
                email: ['', Validators.email],
            }),
            socialMedia: this.fb.group({
                facebook: [''],
                instagram: [''],
                google: [''],
                other: [''],
            }),
            schedules: this.fb.group({
                override: [null],
                schedules: this.fb.array([
                    this.fb.group({
                        type: ['DEFAULT'],
                        schedule: this.fb.group({
                            MONDAY: this.fb.group({ from: ['00:00'], to: ['00:00'] }),
                            TUESDAY: this.fb.group({ from: ['00:00'], to: ['00:00'] }),
                            WEDNESDAY: this.fb.group({ from: ['00:00'], to: ['00:00'] }),
                            THURSDAY: this.fb.group({ from: ['00:00'], to: ['00:00'] }),
                            FRIDAY: this.fb.group({ from: ['00:00'], to: ['00:00'] }),
                            SATURDAY: this.fb.group({ from: ['00:00'], to: ['00:00'] }),
                            SUNDAY: this.fb.group({ from: ['00:00'], to: ['00:00'] }),
                        }),
                    }),
                ]),
                displaySchedules: [false],
            }),
            keywords: this.fb.group({
                en: [''], // Change to string control instead of FormArray
                fr: [''], // Change to string control instead of FormArray
            }),
        });

        if (shop) {
            // Patch basic shop data
            shopForm.patchValue({
                _id: shop._id,
                name: shop.name,
                description: shop.description,
                gatewayToken: shop.gatewayToken,
                website: shop.website,
                onHomepage: shop.onHomepage,
                order: shop.order,
                timezone: shop.timezone,
                likes: shop.likes,
                deleted: shop.deleted,
                address: shop.address,
                contact: shop.contact,
                socialMedia: shop.socialMedia,
                icon: shop.icon,
                // Handle shopCategory and keywords as strings (comma-separated)
                shopCategory: {
                    en: Array.isArray(shop.shopCategory?.en) ? shop.shopCategory.en.join(', ') : shop.shopCategory?.en || '',
                    fr: Array.isArray(shop.shopCategory?.fr) ? shop.shopCategory.fr.join(', ') : shop.shopCategory?.fr || '',
                },
                keywords: {
                    en: Array.isArray(shop.keywords?.en) ? shop.keywords.en.join(', ') : shop.keywords?.en || '',
                    fr: Array.isArray(shop.keywords?.fr) ? shop.keywords.fr.join(', ') : shop.keywords?.fr || '',
                },
            });

            // Handle arrays properly
            this.patchFormArray(shopForm.get('organisations') as FormArray, shop.organisations);
            this.patchFormArray(shopForm.get('pictures') as FormArray, shop.pictures);

            // Handle coordinates array properly
            const coordinatesArray = shopForm.get('address.coordinates.coordinates') as FormArray;
            if (shop.address?.coordinates?.coordinates) {
                coordinatesArray.clear();
                shop.address.coordinates.coordinates.forEach((coord) => {
                    coordinatesArray.push(this.fb.control(coord));
                });
            }

            // Handle schedules (simplified for brevity)
            if (shop.schedules && shop.schedules.schedules.length > 0) {
                const schedulesFormArray = shopForm.get('schedules')?.get('schedules') as FormArray;
                while (schedulesFormArray.length) {
                    schedulesFormArray.removeAt(0);
                }

                shop.schedules.schedules.forEach((schedule) => {
                    const scheduleForm = this.fb.group({
                        type: [schedule.type],
                        schedule: this.fb.group({}),
                    });

                    // Add each day's schedule
                    this.days.forEach((day) => {
                        const daySchedule = schedule.schedule[day as keyof Schedule];
                        if (daySchedule) {
                            (scheduleForm.get('schedule') as FormGroup).addControl(
                                day,
                                this.fb.group({
                                    from: [daySchedule.from],
                                    to: [daySchedule.to],
                                }),
                            );
                        }
                    });

                    schedulesFormArray.push(scheduleForm);
                });

                shopForm.get('schedules')?.get('displaySchedules')?.setValue(shop.schedules.displaySchedules);
            }

            // Handle promotions
            if (shop.promotions && shop.promotions.length > 0) {
                const promotionsArray = shopForm.get('promotions') as FormArray;
                while (promotionsArray.length) {
                    promotionsArray.removeAt(0);
                }

                shop.promotions.forEach((promotion) => {
                    promotionsArray.push(this.createPromotionForm(promotion));
                });
            }

            // Handle items
            if (shop.items && shop.items.length > 0) {
                const itemsArray = shopForm.get('items') as FormArray;
                while (itemsArray.length) {
                    itemsArray.removeAt(0);
                }

                shop.items.forEach((item) => {
                    itemsArray.push(this.createItemForm(item));
                });
            }
        }

        return shopForm;
    }

    // Get schedules FormArray for a specific shop
    getShopSchedules(shopIndex: number): FormArray {
        return (this.shops.at(shopIndex) as FormGroup).get('schedules')?.get('schedules') as FormArray;
    }

    // Helper to patch form arrays
    patchFormArray(formArray: FormArray, values: any[]): void {
        while (formArray.length) {
            formArray.removeAt(0);
        }

        if (values) {
            values.forEach((value) => {
                formArray.push(this.fb.control(value));
            });
        }
    }

    // Add a new shop
    addShop(): void {
        this.shops.push(this.createShopForm());
    }

    // Remove a shop
    removeShop(index: number): void {
        this.shops.removeAt(index);
    }

    // Submit the form
    onSubmit(): void {
        if (this.vendorForm.invalid) {
            this.markFormGroupTouched(this.vendorForm);
            this.snackBar.open('Please fill all required fields', 'Close', { duration: 3000 });
            return;
        }

        this.isSubmitting = true;
        const vendorData = this.prepareVendorData();

        if (this.isEdit && this.data.vendor) {
            this.vendorService.updateVendor(this.data.vendor._id, vendorData).subscribe({
                next: () => {
                    this.snackBar.open('Vendor updated successfully', 'Close', { duration: 3000 });
                    this.dialogRef.close(true);
                },
                error: (error) => {
                    console.error('Error updating vendor', error);
                    this.snackBar.open('Error updating vendor', 'Close', { duration: 3000 });
                    this.isSubmitting = false;
                },
            });
        } else {
            this.vendorService.addVendor(vendorData).subscribe({
                next: () => {
                    this.snackBar.open('Vendor added successfully', 'Close', { duration: 3000 });
                    this.dialogRef.close(true);
                },
                error: (error) => {
                    console.error('Error adding vendor', error);
                    this.snackBar.open('Error adding vendor', 'Close', { duration: 3000 });
                    this.isSubmitting = false;
                },
            });
        }
    }

    // Prepare data for submission
    prepareVendorData(): any {
        const formValue = this.vendorForm.value;

        // If editing, preserve the original ID
        if (this.isEdit && this.data.vendor) {
            return {
                ...formValue,
                _id: this.data.vendor._id,
            };
        }

        return formValue;
    }

    // Mark all form controls as touched to trigger validation
    markFormGroupTouched(formGroup: FormGroup | FormArray): void {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            if (control instanceof FormGroup || control instanceof FormArray) {
                this.markFormGroupTouched(control);
            } else {
                control?.markAsTouched();
            }
        });
    }

    // Close dialog
    onCancel(): void {
        this.dialogRef.close();
    }

    // Handle file selection
    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            this.selectedFile = input.files[0];
        }
    }

    // Handle icon file selection
    onIconFileSelected(event: Event, shopIndex: number): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedIconFile = input.files[0];

            // Validate file type
            if (!this.selectedIconFile.type.startsWith('image/')) {
                this.snackBar.open('Please select a valid image file', 'Close', { duration: 3000 });
                this.selectedIconFile = null;
                return;
            }

            // Optional: Validate file size (e.g., max 5MB)
            if (this.selectedIconFile.size > 5 * 1024 * 1024) {
                this.snackBar.open('File size must be less than 5MB', 'Close', { duration: 3000 });
                this.selectedIconFile = null;
                return;
            }
        }
    }

    // Update the uploadImage method to work with specific indices
    async uploadImage(shopIndex: number, pictureIndex: number): Promise<void> {
        if (!this.selectedFile) {
            this.snackBar.open('No file selected', 'Close', { duration: 3000 });
            return;
        }

        this.isUploading = true;

        try {
            // Convert file to base64
            const base64Content = await this.azureStorageService.convertFileToBase64(this.selectedFile);
            const fileName = this.selectedFile.name;

            this.azureStorageService.uploadShopPicture(fileName, base64Content).subscribe({
                next: (response) => {
                    // Get the picture control at the specific index and set its value to the returned URL
                    if (response.url) {
                        const picturesArray = this.getPictures(shopIndex);
                        picturesArray.at(pictureIndex).setValue(response.url);

                        this.snackBar.open('Image uploaded successfully', 'Close', { duration: 3000 });
                    } else {
                        this.snackBar.open('Upload successful but no URL returned', 'Close', { duration: 3000 });
                    }

                    this.selectedFile = null;
                    this.isUploading = false;
                },
                error: (error) => {
                    console.error('Error uploading image', error);
                    this.snackBar.open('Error uploading image: ' + (error.message || 'Unknown error'), 'Close', { duration: 3000 });
                    this.isUploading = false;
                },
            });
        } catch (error) {
            console.error('Error processing file', error);
            this.snackBar.open('Error processing file', 'Close', { duration: 3000 });
            this.isUploading = false;
        }
    }

    // Upload shop icon
    async uploadShopIcon(shopIndex: number): Promise<void> {
        if (!this.selectedIconFile) {
            this.snackBar.open('Please select a file first', 'Close', { duration: 3000 });
            return;
        }

        this.isUploadingIcon = true;

        try {
            // Convert file to base64
            const base64Content = await this.azureStorageService.convertFileToBase64(this.selectedIconFile);
            const fileName = this.selectedIconFile.name;

            this.azureStorageService.uploadShopIcon(fileName, base64Content).subscribe({
                next: (response) => {
                    if (response.url) {
                        // Set the icon URL in the form
                        const shopFormGroup = this.shops.at(shopIndex) as FormGroup;
                        shopFormGroup.get('icon')?.setValue(response.url);

                        this.snackBar.open('Icon uploaded successfully', 'Close', { duration: 3000 });

                        // Clear the selected file
                        this.selectedIconFile = null;

                        // Reset the file input
                        const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
                        fileInputs.forEach((input) => (input.value = ''));
                    }
                    this.isUploadingIcon = false;
                },
                error: (error) => {
                    console.error('Error uploading icon:', error);
                    this.snackBar.open('Error uploading icon', 'Close', { duration: 3000 });
                    this.isUploadingIcon = false;
                },
            });
        } catch (error) {
            console.error('Error processing file', error);
            this.snackBar.open('Error processing file', 'Close', { duration: 3000 });
            this.isUploading = false;
        }
    }

    // Get pictures FormArray for a specific shop
    getPictures(shopIndex: number): FormArray {
        return (this.shops.at(shopIndex) as FormGroup).get('pictures') as FormArray;
    }

    // Add a new picture field to a shop
    addPicture(shopIndex: number): void {
        const picturesArray = this.getPictures(shopIndex);
        picturesArray.push(this.fb.control(''));
    }

    // Remove a picture from a shop
    removePicture(shopIndex: number, pictureIndex: number): void {
        const picturesArray = this.getPictures(shopIndex);
        picturesArray.removeAt(pictureIndex);
    }

    // Remove shop icon
    removeShopIcon(shopIndex: number): void {
        const shopFormGroup = this.shops.at(shopIndex) as FormGroup;
        shopFormGroup.get('icon')?.setValue('');

        // Clear any selected file
        this.selectedIconFile = null;

        // Reset file input
        const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
        fileInputs.forEach((input) => (input.value = ''));

        this.snackBar.open('Icon removed', 'Close', { duration: 2000 });
    }

    // Create a promotion form group
    createPromotionForm(promotion?: Promotion): FormGroup {
        // Ensure color values have the "#" prefix
        let bgColor = promotion?.backgroundColor || '#FFFFFF';
        let txtColor = promotion?.textColor || '#000000';

        // Add "#" if missing and not empty
        if (bgColor && !bgColor.startsWith('#')) {
            bgColor = '#' + bgColor;
        }

        if (txtColor && !txtColor.startsWith('#')) {
            txtColor = '#' + txtColor;
        }

        const formGroup = this.fb.group({
            tagDescription: this.fb.group({
                en: [promotion?.tagDescription?.en || '', Validators.required],
                fr: [promotion?.tagDescription?.fr || '', Validators.required],
            }),
            fullDescription: this.fb.group({
                en: [promotion?.fullDescription?.en || ''],
                fr: [promotion?.fullDescription?.fr || ''],
            }),
            backgroundColor: [bgColor, Validators.required],
            textColor: [txtColor, Validators.required],
            icon: [promotion?.icon || ''],
            startDate: [promotion?.startDate || new Date().toISOString()],
            endDate: [
                promotion?.endDate ||
                    (() => {
                        const date = new Date();
                        date.setMonth(date.getMonth() + 1);
                        return date.toISOString();
                    })(),
            ],
        });

        // Add _id field only if promotion._id exists
        if (promotion?._id) {
            formGroup.addControl('_id', this.fb.control(promotion._id));
        }

        return formGroup;
    }

    // Get promotions FormArray for a specific shop
    getPromotions(shopIndex: number): FormArray {
        return (this.shops.at(shopIndex) as FormGroup).get('promotions') as FormArray;
    }

    // Add a new promotion to a shop
    addPromotion(shopIndex: number): void {
        const promotionsArray = this.getPromotions(shopIndex);
        promotionsArray.push(this.createPromotionForm());
    }

    // Remove a promotion from a shop
    removePromotion(shopIndex: number, promotionIndex: number): void {
        const promotionsArray = this.getPromotions(shopIndex);
        promotionsArray.removeAt(promotionIndex);
    }

    // Create an item form group
    createItemForm(item?: Item): FormGroup {
        const formGroup = this.fb.group({
            name: this.fb.group({
                en: [item?.name?.en || '', Validators.required],
                fr: [item?.name?.fr || '', Validators.required],
            }),
            description: this.fb.group({
                en: [item?.description?.en || ''],
                fr: [item?.description?.fr || ''],
            }),
            type: [item?.type || ItemType.SUBSCRIPTION, Validators.required],
            categoryType: [item?.categoryType || ItemCategoryType.OTHER, Validators.required],
            isPublic: [item?.isPublic || false],
            deleted: [item?.deleted || false],
            startSale: [item?.startSale || new Date().toISOString()],
            endSale: [item?.endSale || null],
            quantity: [item?.quantity || null],
            quantitySold: [item?.quantitySold || 0],
            quantityBooked: [item?.quantityBooked || 0],
            itemDetails: this.fb.group({
                cost: [item?.itemDetails?.cost || null],
                renewalCost: [item?.itemDetails?.renewalCost || null],
                durationType: [item?.itemDetails?.durationType || DurationType.START_TODAY],
                durationDays: [item?.itemDetails?.durationDays || null],
                fixedStartDate: [item?.itemDetails?.fixedStartDate || null],
                fixedEndDate: [item?.itemDetails?.fixedEndDate || null],
                renewalDurationDays: [item?.itemDetails?.renewalDurationDays || null],
                code: [item?.itemDetails?.code || ''],
                subscriptionType: [item?.itemDetails?.subscriptionType || SubscriptionType.INDIVIDUAL],
                category: [item?.itemDetails?.category || SubscriptionCategory.RE_OTHER],
                cardType: [item?.itemDetails?.cardType || CardType.BCITI],
                isLegacyConnector: [item?.itemDetails?.isLegacyConnector || false],
                customStartDate: [item?.itemDetails?.customStartDate || null],
                shortDescription: this.fb.group({
                    en: [item?.itemDetails?.shortDescription?.en || ''],
                    fr: [item?.itemDetails?.shortDescription?.fr || ''],
                }),
            }),
            fees: this.fb.group({
                fixedAmount: [item?.fees?.fixedAmount || null],
                percentageAmount: [item?.fees?.percentageAmount || null],
                minPrice: [item?.fees?.minPrice || null],
                maxPrice: [item?.fees?.maxPrice || null],
            }),
            conditions: this.fb.group({
                minAge: [item?.conditions?.minAge || null],
                maxAge: [item?.conditions?.maxAge || null],
                card: [item?.conditions?.card || null],
                items: this.fb.array(item?.conditions?.items || []),
            }),
            itemTaxes: this.fb.array(item?.itemTaxes || []),
        });

        // Add _id field only if item._id exists
        if (item?._id) {
            formGroup.addControl('_id', this.fb.control(item._id));
        }

        return formGroup;
    }

    // Get items FormArray for a specific shop
    getItems(shopIndex: number): FormArray {
        return (this.shops.at(shopIndex) as FormGroup).get('items') as FormArray;
    }

    // Add a new item to a shop
    addItem(shopIndex: number): void {
        const itemsArray = this.getItems(shopIndex);
        itemsArray.push(this.createItemForm());
    }

    // Remove an item from a shop
    removeItem(shopIndex: number, itemIndex: number): void {
        const itemsArray = this.getItems(shopIndex);
        itemsArray.removeAt(itemIndex);
    }
}
